import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {Observable, throwError as observableThrowError} from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { BaseService } from '@supoui/core/services/base.service';
import { SettingsService } from '@supoui/core/services/settings.service';
import { environment } from '../../../environments/environment';
import { NGXLogger } from 'ngx-logger';
import moment from 'moment';

export const regexISODate = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2}(?:\.{0,1}\d*))(?:Z|(\+|-)([\d|:]*))?$/;
export const regexISOLocalDate = /^(\d{4})-(\d{2})-(\d{2})$/;

export const convertJSONDate = (value: any): any => {
  if (typeof value === 'string') {
    const result = regexISODate.exec(value);
    if (result) {
      return moment.utc(value).toDate();
    } else {
      const result = regexISOLocalDate.exec(value);
      if (result) {
        return moment.utc(value, 'YYYY-MM-DD');
      }
    }
  }
  return value;
};

export const convertJSONDates = (input: any): any => {
  // Ignore things that aren't objects.
  if (!input || typeof input !== 'object') {
    return convertJSONDate(input);
  }
  Object.keys(input).forEach(key => {
    const value = input[key];
    // Check for string properties which look like dates.
    if (typeof value === 'string') {
      input[key] = convertJSONDate(value);
    } else if (typeof value === 'object') {
      convertJSONDates(value);
    }
  });
  return input;
};

@Injectable({
  providedIn: 'root'
})
export class HttpService extends BaseService {
  private static readonly defaultOptions = {
    headers: new HttpHeaders().set('Content-Type', 'application/json')
  };

  private readonly version: string;

  constructor(private readonly http: HttpClient, protected settingService: SettingsService, private readonly logger: NGXLogger) {
    super(settingService);
    this.version = environment.appVersion;
    if (!this.version.toLowerCase().startsWith('v')) {
      this.version = 'v' + this.version;
    }
  }

  public get<T>(url: string, options?: any): Observable<T> {
    let normalOptions = {
      headers: new HttpHeaders().set('Content-Type', 'application/json')
    };
    return this.http.get<T>(`${this.baseURL}${url}`, Object.assign(normalOptions, options)).pipe(
      map(json => convertJSONDates(json)),
      catchError(error => this.handleError(error))
    );
  }

  public getExternal<T>(url: string, options?: any): Observable<T> {
    let normalOptions = {
      headers: new HttpHeaders().set('Content-Type', 'application/json')
    };
    return this.http.get<T>(`${url}`, Object.assign(normalOptions, options)).pipe(
      map(json => convertJSONDates(json)),
      catchError(error => this.handleError(error))
    );
  }

  public post<T = void>(path: string, data: string | object): Observable<T> {
    return this.http.post<T>(path, data, HttpService.defaultOptions).pipe(catchError(error => this.handleError(error)));
  }

  public put<T = void>(path: string, data: string | object): Observable<T> {
    return this.http.put<T>(`${this.baseURL}${path}`, data, HttpService.defaultOptions).pipe(catchError(error => this.handleError(error)));
  }

  public patch<T = void>(path: string, data: string | object): Observable<T> {
    return this.http.patch<T>(`${this.baseURL}${path}`, data, HttpService.defaultOptions).pipe(catchError(error => this.handleError(error)));
  }

  public delete(params: string): Observable<Object> {
    return this.http.delete(`${this.baseURL}${params}`);
  }

  public getDocument(path: string, id: string, overrideMimeType?: string): Observable<Blob> {
    return this.http.get(`${this.baseURL}${path}${id}`, { responseType: 'arraybuffer', observe: 'response' }).pipe(
      map((res: any) => {
        let mimeType = 'application/pdf';
        if (res.headers.get('content-type')) {
          mimeType = res.headers.get('content-type');
        }
        return new Blob([res.body], {
          type: overrideMimeType ? overrideMimeType : mimeType
        });
      })
    );
  }

  public getVideo(path: string): Observable<Blob> {
    return this.http.get(`${this.baseURL}${path}`, { responseType: 'blob' });
  }

  private handleError(error: any): Observable<never> {
    if (error?.status != '404') {
      this.logger.debug('handleError', error);
    }
    error.status ? `${error.status} - ${error.statusText}` : 'Server error';
    return observableThrowError(error);
  }
}
